/**
 * Learning Center API: typed calls over the shared axios instance
 * (services/api), which attaches the Supabase JWT, X-Site-Id (from the active
 * site) and X-User-Id on every request and handles the 401 refresh and
 * maintenance mode globally. Nothing under src/lms imports axios or builds a
 * second HTTP client (plan 2.3a, 4.8).
 *
 * Every route sits behind the backend guard createLmsGuard (WP-02; X-Site-Id
 * required, super_admin bypasses the entitlement). This file carries the two
 * identity calls of Appendix A (GET /health, GET /me) and the shapes every
 * later api/<area>.ts file shares (the list page, the error readers); each
 * later WP adds its own api/<area>.ts beside it instead of growing this file.
 */
import api from '../../services/api';
import type { LmsBuilderHealth } from './builder';

// ---------------------------------------------------------------------------
// Shared shapes (Appendix A preamble)
// ---------------------------------------------------------------------------

/** Appendix A: lists are {items, page: {limit, offset, hasMore}}, limit at most 200. */
export interface LmsPage {
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface LmsList<T> {
  items: T[];
  page: LmsPage;
}

/** The platform role carried on the users row (backend guard, plan 2.3a). */
export type LmsPlatformRole = 'super_admin' | 'administrator' | 'basic_user';

/**
 * The error body every lms route answers with (plan 2.3a: routes send
 * err.status and { error, code }). A code is present on typed errors
 * (LMS_INVALID, LMS_NOT_FOUND, LMS_ADMIN_REQUIRED, LMS_REAUTH_FAILED, ...).
 */
export interface LmsErrorBody {
  error?: string;
  code?: string;
  [extra: string]: unknown;
}

interface AxiosLikeError {
  response?: { status?: number; data?: LmsErrorBody };
  message?: string;
}

/** The server's error message, else `fallback` (never an empty string). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as AxiosLikeError | null | undefined;
  const msg = e?.response?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}

/** The server's error code (for example 'LMS_REAUTH_FAILED'), or null. */
export function apiErrorCode(err: unknown): string | null {
  const e = err as AxiosLikeError | null | undefined;
  const code = e?.response?.data?.code;
  return typeof code === 'string' && code ? code : null;
}

// ---------------------------------------------------------------------------
// Identity (Appendix A "Health and identity", plan 4.2)
// ---------------------------------------------------------------------------

/**
 * GET /api/lms/me (Appendix A). isLmsAdmin is resolved per request by the
 * guard (plan 4.2, D16): super_admin, platform administrator, or a
 * tool_user_roles row (tool_key 'lms', role 'admin') at the current site.
 * The frontend only uses it to show or hide the two admin tabs; every admin
 * route re-checks server-side.
 */
export interface LmsMe {
  user: {
    id: string;
    /** null when the users row has none (the route sends `?? null`, WP-02). */
    full_name: string | null;
    email: string | null;
    role: LmsPlatformRole;
  };
  isLmsAdmin: boolean;
  siteId: string;
  networkId: string;
}

export async function getMe(): Promise<LmsMe> {
  const response = await api.get<LmsMe>('/api/lms/me');
  return response.data;
}

// ---------------------------------------------------------------------------
// Health (plan 4.10)
// ---------------------------------------------------------------------------

/** One job's last run (plan 4.10); both fields are null until the job has run (or its table exists). */
export interface LmsJobHealth {
  last_at: string | null;
  status: string | null;
}

/**
 * GET /api/lms/health (plan 4.10). The Settings sub-view shows it (WP-48) and
 * the launch runbook reads it as the smoke (WP-72). Fields that depend on a
 * table a later migration creates are null until that table exists.
 */
export interface LmsHealth {
  ok: boolean;
  feature: 'lms';
  notifications_enabled: boolean;
  storage_configured: boolean;
  catalog_pending: number | null;
  jobs: {
    daily: LmsJobHealth | null;
    weekly_digest: LmsJobHealth | null;
  } | null;
  email_failed_24h: number | null;
  /**
   * The course builder block (builder plan 4.7; WP-B04). Every api that ships
   * the builder sends it; optional so a response from an older revision reads
   * as "no builder" (callers check builder?.enabled === true).
   */
  builder?: LmsBuilderHealth;
}

export async function getLmsHealth(): Promise<LmsHealth> {
  const response = await api.get<LmsHealth>('/api/lms/health');
  return response.data;
}
